// pages/master-data/vehicle/VehicleTypeAddEditV2.jsx
'use client';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, CircularProgress, Grid, TextField, Button, Typography, InputAdornment, Divider } from '@mui/material';
import { AxleLivePreview } from './AxleBuilder';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { getVehicleTypeById, createVehicleType, updateVehicleType } from '../../../stores/vehicleTypeStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import PickerField from '../../../components/PickerField';
import MasterCompanyAddEdit from '../company/MasterCompanyAddEdit';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';
import { useAxleTypeList, getAxleTypeById } from '../../../stores/axleTypeStore';
import AxleBuilderAddEdit from '../axle-builder/AxleBuilderAddEdit';

const VehicleTypeAddEditV2 = forwardRef(
  ({ open, id, onClose, onSuccess, onError, onSubmitStart = () => {}, onSubmitEnd = () => {} }, ref) => {
    const { t } = useLanguage();
    const notify = useNotifier();
    const formikRef = useRef();
    const [loading, setLoading] = useState(false);

    const companyPickerParams = usePickerPagination({
      fetchHook: useCompanyDataList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name'
    });

    const axleTypePickerParams = usePickerPagination({
      fetchHook: useAxleTypeList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name',
      formatOption: (item) => ({
        value: String(item.id),
        label: item.name,
        row: item
      })
    });

    const [initialValues, setInitialValues] = useState({
      company: null,
      name: '',
      length_dimension: '',
      width_dimension: '',
      height_dimension: '',
      load_capacity: '',
      min_speed: '',
      average_speed: '',
      max_speed: '',
      description: '',
      axle_type: null,
      axles: []
    });

    useImperativeHandle(ref, () => ({
      submit: () => {
        if (formikRef.current) {
          formikRef.current.submitForm();
        }
      }
    }));

    useEffect(() => {
      const fetchData = async () => {
        if (!id || !open) return;
        setLoading(true);
        try {
          const data = await getVehicleTypeById(id);
          if (data) {
            let initialAxles = data.axles || [];
            if (data.axle_type && data.axle_type > 0 && initialAxles.length === 0) {
              try {
                const axleRes = await getAxleTypeById(data.axle_type);
                let configObj = axleRes?.axle_configuration;
                if (typeof configObj === 'string') {
                  try {
                    configObj = JSON.parse(configObj);
                  } catch {}
                }
                if (configObj && Array.isArray(configObj.info)) initialAxles = configObj.info;
                else if (Array.isArray(axleRes?.axles)) initialAxles = axleRes.axles;
              } catch (e) {
                console.error('Failed to fetch axle layout', e);
              }
            }

            setInitialValues({
              company: data.company ?? null,
              name: data.name || '',
              length_dimension: data.length_dimension ?? '',
              width_dimension: data.width_dimension ?? '',
              height_dimension: data.height_dimension ?? '',
              load_capacity: data.load_capacity ?? '',
              min_speed: data.min_speed ?? '',
              average_speed: data.average_speed ?? '',
              max_speed: data.max_speed ?? '',
              description: data.description ?? '',
              axle_type: data.axle_type ?? null,
              axles: initialAxles
            });
          }
        } catch {
          notify({ message: 'Gagal mengambil data', severity: 'error' });
        } finally {
          setLoading(false);
        }
      };

      if (open) {
        if (!id) {
          let defaultCompany = null;
          try {
            const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
            if (stored && stored !== 'all') {
              defaultCompany = Number(stored);
            } else {
              const rawUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
              if (rawUser) {
                const u = JSON.parse(rawUser);
                if (u?.company || u?.company_id) {
                  defaultCompany = Number(u.company || u.company_id);
                }
              }
            }
          } catch (e) {
            console.error('Failed to parse default company', e);
          }

          const defaultValues = {
            company: defaultCompany,
            name: '',
            length_dimension: '',
            width_dimension: '',
            height_dimension: '',
            load_capacity: '',
            min_speed: '',
            average_speed: '',
            max_speed: '',
            description: '',
            axle_type: null,
            axles: []
          };
          setInitialValues(defaultValues);
          setTimeout(() => {
            if (formikRef.current) {
              formikRef.current.resetForm({ values: defaultValues });
            }
          }, 0);
        }
        fetchData();
      }
    }, [id, notify, open]);

    const validationSchema = Yup.object().shape({
      company: Yup.mixed().required(t('validation.required', 'Required')),
      name: Yup.string().required(t('validation.required', 'Required')),
      length_dimension: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .required(t('validation.required', 'Required')),
      width_dimension: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .required(t('validation.required', 'Required')),
      height_dimension: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .required(t('validation.required', 'Required')),
      load_capacity: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .required(t('validation.required', 'Required')),
      min_speed: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .test('is-less-than-max', 'Min Speed cannot be greater than Max Speed', function (value) {
          const { max_speed } = this.parent;
          if (value == null || max_speed == null || max_speed === '') return true;
          return value <= max_speed;
        })
        .required(t('validation.required', 'Required')),
      max_speed: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .test('is-greater-than-min', 'Max Speed cannot be less than Min Speed', function (value) {
          const { min_speed } = this.parent;
          if (value == null || min_speed == null || min_speed === '') return true;
          return value >= min_speed;
        })
        .required(t('validation.required', 'Required')),
      average_speed: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .test('is-logical-average', 'Average Speed must be between Min and Max Speed', function (value) {
          const { min_speed, max_speed } = this.parent;
          if (value == null) return true;
          let isValid = true;
          if (min_speed != null && min_speed !== '') isValid = isValid && value >= min_speed;
          if (max_speed != null && max_speed !== '') isValid = isValid && value <= max_speed;
          return isValid;
        })
        .required(t('validation.required', 'Required')),
      description: Yup.string().nullable(),
      axle_type: Yup.number().nullable().required(t('validation.required', 'Required'))
    });

    const normalizeCompanyValue = (companyValue) => {
      if (companyValue === '' || companyValue === null || companyValue === undefined) return null;
      if (typeof companyValue === 'object') {
        if ('value' in companyValue) return Number(companyValue.value);
        if ('id' in companyValue) return Number(companyValue.id);
        return null;
      }
      const n = Number(companyValue);
      return Number.isFinite(n) ? Math.trunc(n) : null;
    };

    const handleSubmit = async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSubmitStart();
      try {
        const companyId = normalizeCompanyValue(values.company);
        if (companyId === null) {
          throw new Error('Company id is missing or invalid');
        }
        const payload = {
          company: companyId,
          name: formatUpperCase(values.name) ?? '',
          length_dimension: values.length_dimension === '' ? null : Number(values.length_dimension),
          width_dimension: values.width_dimension === '' ? null : Number(values.width_dimension),
          height_dimension: values.height_dimension === '' ? null : Number(values.height_dimension),
          load_capacity: values.load_capacity === '' ? null : Number(values.load_capacity),
          max_speed: values.max_speed === '' ? null : Number(values.max_speed),
          min_speed: values.min_speed === '' ? null : Number(values.min_speed),
          average_speed: values.average_speed === '' ? null : Number(values.average_speed),
          description: values.description ?? '',
          axle_type: values.axle_type ? Number(values.axle_type) : 0
        };

        let response;
        if (id) {
          response = await updateVehicleType(id, payload);
          onSuccess && onSuccess('Updated successfully');
        } else {
          response = await createVehicleType(payload);
          onSuccess && onSuccess('Created successfully');
        }

        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }

        onClose && onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    return (
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
          <FormDialog
            open={open}
            onClose={onClose}
            title={id ? t('vehicleType.edit', 'Edit Vehicle Type') : t('vehicleType.add', 'Add Vehicle Type')}
            fullScreenMode
            fullHeight={false}
            actions={
              <>
                <Button
                  variant="contained"
                  type="submit"
                  form="vehicle-type-form"
                  disabled={isSubmitting || loading}
                  sx={{ backgroundColor: '#0072DA', textTransform: 'none' }}
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                </Button>
                <Button onClick={onClose} disableRipple disableElevation variant="outlined" sx={{ textTransform: 'none' }}>
                  {t('buttonTitle.cancel', 'Cancel')}
                </Button>
              </>
            }
          >
            {loading || companyPickerParams.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: 200, alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <form id="vehicle-type-form" onSubmit={handleSubmit}>
                {/* LEFT COLUMN: Form Fields & Axle Config */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 6 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
                      General Information
                    </Typography>
                    <Grid size={{ xs: 12 }}>
                      <PickerField
                        label={t('general.company', 'Company')}
                        name="company"
                        value={values.company ?? null}
                        {...companyPickerParams}
                        allowAdd
                        AddComponent={MasterCompanyAddEdit}
                        onChange={(val) => {
                          const v = typeof val === 'object' && val !== null ? val.value : val;
                          setFieldValue('company', v);
                          try {
                            if (v !== null && v !== undefined && v !== '') {
                              localStorage.setItem('companyId', String(v));
                            }
                          } catch {}
                        }}
                        error={touched.company && !!errors.company}
                        helperText={touched.company && errors.company}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }} mt={1}>
                      <TextField
                        fullWidth
                        size="medium"
                        id="name"
                        name="name"
                        label={t('vehicleType.name', 'Name')}
                        value={values.name}
                        onChange={(e) => setFieldValue('name', formatUpperCase(e.target.value))}
                        onBlur={handleBlur}
                        error={touched.name && !!errors.name}
                        helperText={touched.name && errors.name}
                        required
                        InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }} mt={1}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        id="description"
                        name="description"
                        label={t('general.description', 'Description')}
                        value={values.description ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.description && !!errors.description}
                        helperText={touched.description && errors.description}
                      />
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 6, md: 6 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
                      Dimensions & Capacity
                    </Typography>
                    <Grid container spacing={2} size={{ xs: 12, md: 12 }}>
                      <Grid item size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          size="medium"
                          id="length_dimension"
                          name="length_dimension"
                          label={t('vehicleType.length', 'Length')}
                          type="number"
                          InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                          inputProps={{ min: 0 }}
                          value={values.length_dimension ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.length_dimension && !!errors.length_dimension}
                          helperText={touched.length_dimension && errors.length_dimension}
                          required
                          InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          size="medium"
                          id="width_dimension"
                          name="width_dimension"
                          label={t('vehicleType.width', 'Width')}
                          type="number"
                          InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                          inputProps={{ min: 0 }}
                          value={values.width_dimension ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.width_dimension && !!errors.width_dimension}
                          helperText={touched.width_dimension && errors.width_dimension}
                          required
                          InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          size="medium"
                          id="height_dimension"
                          name="height_dimension"
                          label={t('vehicleType.height', 'Height')}
                          type="number"
                          InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                          inputProps={{ min: 0 }}
                          value={values.height_dimension ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.height_dimension && !!errors.height_dimension}
                          helperText={touched.height_dimension && errors.height_dimension}
                          required
                          InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 12 }} mt={1}>
                      <TextField
                        fullWidth
                        size="medium"
                        id="load_capacity"
                        name="load_capacity"
                        label={t('vehicleType.loadCapacity', 'Load Capacity')}
                        type="number"
                        InputProps={{ endAdornment: <InputAdornment position="end">Kg</InputAdornment> }}
                        inputProps={{ min: 0 }}
                        value={values.load_capacity ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.load_capacity && !!errors.load_capacity}
                        helperText={touched.load_capacity && errors.load_capacity}
                        required
                        InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                      />
                    </Grid>
                    {/* SPEED LIMITS */}

                    <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 700, color: 'primary.main' }}>
                      Speed Configuration
                    </Typography>
                    <Grid container spacing={2} size={{ xs: 12, md: 12 }}>
                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          size="medium"
                          id="min_speed"
                          name="min_speed"
                          label={t('vehicleType.minSpeed', 'Min Speed')}
                          type="number"
                          InputProps={{ endAdornment: <InputAdornment position="end">km/h</InputAdornment> }}
                          inputProps={{ min: 0 }}
                          value={values.min_speed ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.min_speed && !!errors.min_speed}
                          helperText={touched.min_speed && errors.min_speed}
                          required
                          InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                        />
                      </Grid>

                      <Grid item size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          size="medium"
                          id="max_speed"
                          name="max_speed"
                          label={t('vehicleType.maxSpeed', 'Max Speed')}
                          type="number"
                          InputProps={{ endAdornment: <InputAdornment position="end">km/h</InputAdornment> }}
                          inputProps={{ min: 0 }}
                          value={values.max_speed ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.max_speed && !!errors.max_speed}
                          helperText={touched.max_speed && errors.max_speed}
                          required
                          InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item size={{ xs: 12, md: 12 }} mt={1}>
                      <TextField
                        fullWidth
                        size="medium"
                        id="average_speed"
                        name="average_speed"
                        label={t('vehicleType.averageSpeed', 'Average Speed')}
                        type="number"
                        InputProps={{ endAdornment: <InputAdornment position="end">km/h</InputAdornment> }}
                        inputProps={{ min: 0 }}
                        value={values.average_speed ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.average_speed && !!errors.average_speed}
                        helperText={touched.average_speed && errors.average_speed}
                        required
                        InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Divider sx={{ mt: 4, borderStyle: 'dashed', borderColor: '#cbd5e1' }} />
                <Grid container spacing={2} mt={4}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
                      Axle Layout
                    </Typography>
                    <PickerField
                      label="Axle Layout (Type) *"
                      name="axle_type"
                      value={values.axle_type ?? null}
                      {...axleTypePickerParams}
                      allowAdd
                      AddComponent={AxleBuilderAddEdit}
                      onChange={(val) => {
                        setFieldValue('axle_type', val);
                        let parsedAxles = [];
                        const option = axleTypePickerParams.options.find((o) => String(o.value) === String(val));
                        if (option?.row) {
                          let configObj = option.row.axle_configuration;
                          if (typeof configObj === 'string') {
                            try {
                              configObj = JSON.parse(configObj);
                            } catch {}
                          }
                          if (configObj && Array.isArray(configObj.info)) parsedAxles = configObj.info;
                          else if (Array.isArray(option.row.axles)) parsedAxles = option.row.axles;
                        }
                        setFieldValue('axles', parsedAxles);
                      }}
                      error={touched.axle_type && !!errors.axle_type}
                      helperText={touched.axle_type && errors.axle_type}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AxleLivePreview axles={values.axles || []} />
                  </Grid>
                </Grid>
              </form>
            )}
          </FormDialog>
        )}
      </Formik>
    );
  }
);

export default VehicleTypeAddEditV2;
